import { Check, ChevronDown, ChevronUp } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { Button } from '@/shared/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import { FieldError } from '@/shared/components/ui/field-error'

import ProductRow from './ProductRow'

const TAX_OPTIONS = [
  {
    value: 'cgst_sgst',
    label: 'CGST + SGST',
  },
  {
    value: 'igst',
    label: 'IGST',
  },
  {
    value: 'export_lut_igst_sgst_cgst',
    label:
      'SUPPLY MEANT FOR EXPORT/SUPPLY TO SEZ UNIT OR SEZ DEVELOPER FOR AUTHORISED OPERATIONS UNDER BOND OR LETTER OF UNDERTAKING WITHOUT PAYMENT OF IGST/SGST/CGST',
  },
]

const EXPORT_TAX_TYPES = ['export_lut_igst_sgst_cgst']

export default function ProductDetailsCard({
  products,
  errors = {},
  onAddProduct,
  onRemoveProduct,
  onProductChange,
  taxType = 'cgst_sgst',
  onTaxTypeChange,

  // Lead
  isLeadSelected = false,

  // Delivery Charge
  deliveryCharge = '',
  showDeliveryCharge = false,
  onAddDeliveryCharge,
  onDeliveryChargeChange,
  onRemoveDeliveryCharge,
}) {
  const [isTaxDropdownOpen, setIsTaxDropdownOpen] = useState(false)

  const dropdownRef = useRef(null)

  const selectedTaxOption =
    TAX_OPTIONS.find((option) => option.value === taxType) || TAX_OPTIONS[0]

  const isExportSupply = EXPORT_TAX_TYPES.includes(taxType)

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsTaxDropdownOpen(false)
      }
    }

    if (isTaxDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isTaxDropdownOpen])

  const handleTaxTypeChange = (value) => {
    onTaxTypeChange(value)
    setIsTaxDropdownOpen(false)
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <CardTitle>Product Details</CardTitle>

            {isLeadSelected && (
              <span className="text-[11px] font-medium text-slate-400">
                From Lead
              </span>
            )}
          </div>

          <div className="ml-auto flex w-full items-center justify-end gap-3 sm:w-auto">
            <label
              htmlFor="taxType"
              className="shrink-0 text-sm font-medium text-gray-700"
            >
              Tax Type
            </label>

            {/* Custom Tax Type Dropdown */}
            <div ref={dropdownRef} className="relative w-full sm:w-[420px]">
              <button
                type="button"
                id="taxType"
                onClick={() => setIsTaxDropdownOpen((previous) => !previous)}
                className="flex min-h-10 w-full items-center justify-between gap-3 rounded-lg border border-gray-300 bg-white px-3 py-2 text-left text-sm shadow-sm outline-none transition hover:border-gray-400 focus:border-[#1F3FAF] focus:ring-2 focus:ring-[#1F3FAF]/20"
              >
                <span className="min-w-0 truncate text-gray-800">
                  {selectedTaxOption.label}
                </span>

                {isTaxDropdownOpen ? (
                  <ChevronUp className="h-4 w-4 shrink-0 text-gray-500" />
                ) : (
                  <ChevronDown className="h-4 w-4 shrink-0 text-gray-500" />
                )}
              </button>

              {isTaxDropdownOpen && (
                <div className="absolute right-0 z-50 mt-2 max-h-[360px] w-full overflow-y-auto rounded-xl border border-gray-200 bg-white p-1.5 shadow-xl">
                  {TAX_OPTIONS.map((option) => {
                    const isSelected = option.value === taxType

                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleTaxTypeChange(option.value)}
                        className={`flex w-full items-start gap-3 rounded-lg px-3 py-3 text-left text-sm transition ${
                          isSelected
                            ? 'bg-[#1F3FAF]/10 text-[#1F3FAF]'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
                          {isSelected && (
                            <Check className="h-4 w-4 text-[#1F3FAF]" />
                          )}
                        </span>

                        <span className="leading-5">{option.label}</span>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <FieldError message={errors.products} />

        <div className="overflow-x-auto rounded-xl border">
          <table className="w-full min-w-[950px] border-collapse">
            <thead className="bg-[#1F3FAF] text-white">
              <tr>
                <th className="px-3 py-2 text-left text-sm font-semibold">
                  Product
                </th>

                <th className="px-3 py-2 text-left text-sm font-semibold">
                  HSN
                </th>

                <th className="px-3 py-2 text-right text-sm font-semibold">
                  Unit Price
                </th>

                <th className="px-3 py-2 text-center text-sm font-semibold">
                  Qty
                </th>

                <th className="px-3 py-2 text-right text-sm font-semibold">
                  Before GST
                </th>

                {taxType === 'cgst_sgst' ? (
                  <>
                    <th className="px-3 py-2 text-center text-sm font-semibold">
                      CGST %
                    </th>

                    <th className="px-3 py-2 text-center text-sm font-semibold">
                      SGST %
                    </th>
                  </>
                ) : (
                  <th className="px-3 py-2 text-center text-sm font-semibold">
                    {isExportSupply ? 'Tax' : 'IGST %'}
                  </th>
                )}

                <th className="px-3 py-2 text-right text-sm font-semibold">
                  Total
                </th>

                <th className="px-3 py-2 text-center text-sm font-semibold">
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {products.map((product, index) => (
                <ProductRow
                  key={index}
                  product={product}
                  index={index}
                  errors={errors}
                  onChange={onProductChange}
                  onRemove={onRemoveProduct}
                  taxType={taxType}
                  isLeadSelected={isLeadSelected}
                />
              ))}
            </tbody>
          </table>
        </div>

        {/* Product / Delivery Charge Actions */}
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <Button type="button" variant="outline" onClick={onAddProduct}>
            + Add Product
          </Button>

          {!showDeliveryCharge ? (
            <Button
              type="button"
              variant="outline"
              onClick={onAddDeliveryCharge}
            >
              + Add Delivery Charge
            </Button>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center overflow-hidden rounded-lg border border-gray-300 bg-white">
                <span className="border-r bg-gray-50 px-3 py-2 text-sm text-gray-500">
                  ₹
                </span>

                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={deliveryCharge}
                  onChange={(e) => onDeliveryChargeChange(e.target.value)}
                  placeholder="0"
                  className="w-[150px] px-3 py-2 text-sm outline-none"
                />
              </div>

              <Button
                type="button"
                variant="ghost"
                onClick={onRemoveDeliveryCharge}
                className="text-red-500 hover:bg-red-50 hover:text-red-600"
              >
                Remove
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
